import React, { useMemo } from 'react';

const DashboardContent = ({ pagesPermissions = [], user = null, onMenuChange }) => {
  // Static menu items definition for icons and labels
  const allMenuItems = {
    'smart-parser': { 
      icon: 'fa-robot', 
      label: 'PDF Parser Smart',
      description: 'Upload dan analisis dokumen PDF dengan AI untuk ekstraksi data otomatis',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      tags: ['AI', 'Parsing', 'Automation']
    },
    'financial-analysis': { 
      icon: 'fa-file-invoice-dollar', 
      label: 'Laporan Keuangan Analysis',
      description: 'Analisis laporan keuangan dengan AI untuk insights finansial',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      tags: ['Analysis', 'Financial', 'Reporting']
    },
    'audit-plan-analysis': { 
      icon: 'fa-clipboard-check', 
      label: 'Audit Plan Analysis',
      description: 'Analisis dokumen regulasi dan internal untuk perencanaan audit',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      tags: ['Analysis', 'Audit', 'Compliance']
    },
    'rnd-prompting': { 
      icon: 'fa-flask', 
      label: 'RnD Prompting',
      description: 'Testing dan eksperimen dengan AI prompts untuk penelitian',
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      tags: ['AI', 'Research', 'Testing']
    },
    'llm-config': { 
      icon: 'fa-microchip', 
      label: 'LLM Configuration',
      description: 'Konfigurasi model AI dan parameter untuk penggunaan optimal',
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600',
      tags: ['AI', 'Configuration', 'Settings']
    },
    'analytics': { 
      icon: 'fa-chart-line', 
      label: 'Analytics',
      description: 'Dashboard analytics dan insights dari penggunaan sistem',
      color: 'bg-pink-500',
      hoverColor: 'hover:bg-pink-600',
      tags: ['Analytics', 'Dashboard', 'Insights']
    },
    'history': { 
      icon: 'fa-clock-rotate-left', 
      label: 'History',
      description: 'Riwayat semua aktivitas dan analisis yang telah dilakukan',
      color: 'bg-gray-500',
      hoverColor: 'hover:bg-gray-600',
      tags: ['History', 'Records', 'Logs']
    },
    'admin-area': { 
      icon: 'fa-shield-halved', 
      label: 'Admin Area',
      description: 'Manajemen users, groups, dan permissions untuk administrator',
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      tags: ['Administration', 'Management', 'Security']
    }
  };

  // Get function tags for a page slug
  const getFunctionTags = (slug) => {
    const menuItem = allMenuItems[slug];
    return menuItem?.tags || ['Feature'];
  };

  // Tag color mapping with dark mode support
  const getTagColor = (tag) => {
    const tagColors = {
      'AI': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
      'Analysis': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      'Parsing': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
      'Automation': 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
      'Financial': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      'Reporting': 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300',
      'Audit': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
      'Compliance': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
      'Research': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
      'Testing': 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300',
      'Configuration': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      'Settings': 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
      'Analytics': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      'Dashboard': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
      'Insights': 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300',
      'History': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      'Records': 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
      'Logs': 'bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300',
      'Administration': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      'Management': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
      'Security': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      'Feature': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
    };
    return tagColors[tag] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  // Get accessible pages with their permissions
  const accessiblePages = useMemo(() => {
    if (!pagesPermissions || pagesPermissions.length === 0) {
      return [];
    }

    return pagesPermissions
      .filter(pageWithPerm => {
        // Only include pages with can_view permission and is_active
        return pageWithPerm.permissions && 
               pageWithPerm.permissions.can_view === true && 
               pageWithPerm.page && 
               pageWithPerm.page.is_active;
      })
      .map(pageWithPerm => {
        const slug = pageWithPerm.page.slug;
        const menuItem = allMenuItems[slug] || {
          icon: pageWithPerm.page.icon || 'fa-file',
          label: pageWithPerm.page.name,
          description: pageWithPerm.page.description || `Akses ke halaman ${pageWithPerm.page.name}`,
          color: 'bg-gray-500',
          hoverColor: 'hover:bg-gray-600'
        };

        return {
          ...pageWithPerm,
          menuItem: menuItem,
          slug: slug
        };
      })
      .sort((a, b) => {
        // Sort by sort_order if available, otherwise alphabetically
        const orderA = a.page.sort_order || 999;
        const orderB = b.page.sort_order || 999;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return a.page.name.localeCompare(b.page.name);
      });
  }, [pagesPermissions]);

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return 'Pengguna';
    return user.full_name || user.username || user.email?.split('@')[0] || 'Pengguna';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6 transition-colors duration-300">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2 transition-colors duration-300">
              Selamat Datang, {getUserDisplayName()}! 👋
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-lg transition-colors duration-300">
              Dashboard utama untuk mengakses semua fitur yang tersedia
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 border-l-4 border-blue-500 dark:border-blue-400 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium transition-colors duration-300">Total Fitur Akses</p>
                <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2 transition-colors duration-300">{accessiblePages.length}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-4">
                <i className="fa-solid fa-list text-blue-600 text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 border-l-4 border-green-500 dark:border-green-400 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium transition-colors duration-300">Status Akun</p>
                <p className="text-2xl font-bold text-gray-800 dark:text-white mt-2 transition-colors duration-300">
                  {user?.is_active ? 'Aktif' : 'Tidak Aktif'}
                </p>
              </div>
              <div className="bg-green-100 rounded-full p-4">
                <i className={`fa-solid ${user?.is_active ? 'fa-check-circle' : 'fa-times-circle'} text-green-600 text-2xl`}></i>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 border-l-4 border-purple-500 dark:border-purple-400 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium transition-colors duration-300">Role</p>
                <p className="text-2xl font-bold text-gray-800 dark:text-white mt-2 transition-colors duration-300">
                  {user?.is_superuser ? 'Superuser' : 'User'}
                </p>
              </div>
              <div className="bg-purple-100 rounded-full p-4">
                <i className={`fa-solid ${user?.is_superuser ? 'fa-crown' : 'fa-user'} text-purple-600 text-2xl`}></i>
              </div>
            </div>
          </div>
        </div>

        {/* Accessible Pages Grid */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center transition-colors duration-300">
              <i className="fa-solid fa-rocket mr-3 text-blue-500"></i>
              Fitur yang Dapat Diakses
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
              {accessiblePages.length} fitur tersedia
            </span>
          </div>

          {accessiblePages.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-12 text-center transition-colors duration-300">
              <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <i className="fa-solid fa-inbox text-gray-400 text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Belum Ada Fitur yang Dapat Diakses
              </h3>
              <p className="text-gray-500">
                Hubungi administrator untuk mendapatkan akses ke fitur
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {accessiblePages.map((pageData) => {
                const { page, menuItem, permissions } = pageData;
                const isAdminArea = page.slug === 'admin-area';

                return (
                  <div
                    key={page.id || page.slug}
                    onClick={() => onMenuChange && onMenuChange(page.slug)}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 hover:shadow-lg transition-all duration-300 cursor-pointer group border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 overflow-hidden"
                  >
                    {/* Card Header with Icon */}
                    <div className={`${menuItem.color} ${menuItem.hoverColor} p-6 text-white relative overflow-hidden`}>
                      <div className="absolute top-0 right-0 -mr-12 -mt-12 w-32 h-32 bg-white dark:bg-gray-700 opacity-10 rounded-full"></div>
                      <div className="absolute bottom-0 left-0 -ml-8 -mb-8 w-24 h-24 bg-white dark:bg-gray-700 opacity-10 rounded-full"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <div className={`bg-white bg-opacity-20 rounded-lg p-3`}>
                            <i className={`fa-solid ${menuItem.icon} text-3xl`}></i>
                          </div>
                          {isAdminArea && (
                            <span className="bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                              ADMIN
                            </span>
                          )}
                        </div>
                        <h3 className="text-xl font-bold mb-1 text-white transition-colors duration-300">{menuItem.label}</h3>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-6">
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2 transition-colors duration-300">
                        {menuItem.description}
                      </p>

                      {/* Function Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {getFunctionTags(page.slug).map((tag, index) => (
                          <span 
                            key={index}
                            className={`${getTagColor(tag)} text-xs font-medium px-2 py-1 rounded`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Action Button */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                        <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
                          Klik untuk membuka
                        </span>
                        <div className="text-blue-500 group-hover:translate-x-1 transition-transform duration-300">
                          <i className="fa-solid fa-arrow-right"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-8 transition-colors duration-300">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2 flex items-center transition-colors duration-300">
                <i className="fa-solid fa-circle-question mr-3 text-blue-500 dark:text-blue-400"></i>
                Butuh Bantuan?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-lg transition-colors duration-300">
                Kami siap membantu Anda. Pilih opsi di bawah ini untuk mendapatkan bantuan yang Anda butuhkan.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md dark:hover:shadow-gray-900/50 transition-all duration-300">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full w-12 h-12 flex items-center justify-center mb-4 transition-colors duration-300">
                  <i className="fa-solid fa-book text-blue-600 dark:text-blue-400 text-xl transition-colors duration-300"></i>
                </div>
                <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Dokumentasi</h4>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 transition-colors duration-300">
                  Pelajari cara menggunakan semua fitur sistem melalui panduan lengkap dan tutorial.
                </p>
                <button
                  onClick={() => onMenuChange && onMenuChange('documentation')}
                  className="w-full bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  <i className="fa-solid fa-book mr-2"></i>
                  Buka Dokumentasi
                </button>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md dark:hover:shadow-gray-900/50 transition-all duration-300">
                <div className="bg-green-100 dark:bg-green-900/30 rounded-full w-12 h-12 flex items-center justify-center mb-4 transition-colors duration-300">
                  <i className="fa-solid fa-envelope text-green-600 dark:text-green-400 text-xl transition-colors duration-300"></i>
                </div>
                <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Hubungi Kami</h4>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 transition-colors duration-300">
                  Hubungi tim support kami jika Anda mengalami masalah atau memiliki pertanyaan khusus.
                </p>
                <button
                  onClick={() => onMenuChange && onMenuChange('contact-us')}
                  className="w-full bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  <i className="fa-solid fa-envelope mr-2"></i>
                  Kirim Pesan
                </button>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6 transition-colors duration-300">
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
                <i className="fa-solid fa-circle-info mr-2 text-gray-500 dark:text-gray-400"></i>
                Informasi Bantuan Lainnya
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors duration-300">
                  <i className="fa-solid fa-question-circle text-blue-500 dark:text-blue-400 mr-3 mt-1 transition-colors duration-300"></i>
                  <div>
                    <h5 className="font-medium text-gray-800 dark:text-white mb-1 transition-colors duration-300">FAQ (Frequently Asked Questions)</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">
                      Cari jawaban untuk pertanyaan yang sering diajukan tentang penggunaan sistem.
                    </p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors duration-300">
                  <i className="fa-solid fa-video text-green-500 dark:text-green-400 mr-3 mt-1 transition-colors duration-300"></i>
                  <div>
                    <h5 className="font-medium text-gray-800 dark:text-white mb-1 transition-colors duration-300">Video Tutorial</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">
                      Tonton video tutorial untuk mempelajari fitur-fitur utama sistem secara visual.
                    </p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors duration-300">
                  <i className="fa-solid fa-comments text-purple-500 dark:text-purple-400 mr-3 mt-1 transition-colors duration-300"></i>
                  <div>
                    <h5 className="font-medium text-gray-800 dark:text-white mb-1 transition-colors duration-300">Forum Diskusi</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">
                      Bergabung dengan komunitas pengguna untuk berbagi tips dan pengalaman.
                    </p>
                  </div>
                </div>
                <div className="flex items-start p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors duration-300">
                  <i className="fa-solid fa-bug text-red-500 dark:text-red-400 mr-3 mt-1 transition-colors duration-300"></i>
                  <div>
                    <h5 className="font-medium text-gray-800 dark:text-white mb-1 transition-colors duration-300">Laporkan Bug</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">
                      Laporkan masalah teknis atau bug yang Anda temukan untuk membantu perbaikan.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors duration-300">
              <div className="flex items-start">
                <i className="fa-solid fa-lightbulb text-blue-600 dark:text-blue-400 mr-3 mt-1 transition-colors duration-300"></i>
                <div>
                  <h5 className="font-semibold text-blue-900 dark:text-blue-200 mb-1 transition-colors duration-300">Tips Cepat</h5>
                  <p className="text-sm text-blue-800 dark:text-blue-300 transition-colors duration-300">
                    Jika Anda mengalami masalah, coba refresh halaman terlebih dahulu. Jika masalah masih berlanjut, 
                    silakan hubungi administrator atau tim support kami. Kami akan merespons dalam waktu 24 jam.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;

